import React, { FC, PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { IActivity } from './useAssistant';
import styles from './mascot.module.scss';

const POSITION_KEY = 'swiftlane.mascot.position';
const BOUNCED_KEY = 'swiftlane.mascot.bounced';
const SIZE = { width: 96, height: 120 };
const MARGIN = 16;
const DRAG_THRESHOLD = 5;
/** While idle, a short hop every 10 seconds so admins notice it is there. */
const NUDGE_EVERY_MS = 10_000;
const NUDGE_MS = 1400;

interface IPosition {
    /** Offsets from the bottom-right corner, so the mascot stays near the corner on resize. */
    right: number;
    bottom: number;
}

const DEFAULT_POSITION: IPosition = { right: 24, bottom: 20 };

const readPosition = (): IPosition => {
    try {
        const raw = localStorage.getItem(POSITION_KEY);
        if (!raw) return DEFAULT_POSITION;
        const parsed = JSON.parse(raw);
        if (typeof parsed.right === 'number' && typeof parsed.bottom === 'number') return parsed;
    } catch (e) {
        /* ignore */
    }
    return DEFAULT_POSITION;
};

const clamp = (position: IPosition): IPosition => ({
    right: Math.min(Math.max(position.right, MARGIN), Math.max(MARGIN, window.innerWidth - SIZE.width - MARGIN)),
    bottom: Math.min(Math.max(position.bottom, MARGIN), Math.max(MARGIN, window.innerHeight - SIZE.height - MARGIN)),
});

interface IMascotProps {
    onOpen: () => void;
    activity: IActivity;
    streaming: boolean;
    hasPending: boolean;
}

/**
 * The Swiftlane logo with legs, arms and a face: the assistant's presence on the page.
 * Drops in once per session, idles in the corner, waves on hover, taps its chin while a
 * turn runs, bounces when an approval is waiting. Drag it anywhere; double-click resets.
 */
const Mascot: FC<IMascotProps> = ({ onOpen, activity, streaming, hasPending }) => {
    const [position, setPosition] = useState<IPosition>(() => clamp(readPosition()));
    const [entering, setEntering] = useState(() => !sessionStorage.getItem(BOUNCED_KEY));
    const [dragging, setDragging] = useState(false);
    const [hover, setHover] = useState(false);
    const [nudging, setNudging] = useState(false);
    const dragRef = useRef<{ startX: number; startY: number; right: number; bottom: number; moved: boolean } | null>(null);
    // a click opens after a short delay so a double-click (reset) does not also open the chat
    const openTimerRef = useRef<number | null>(null);

    // the login bounce plays once per browser session
    useEffect(() => {
        if (!entering) return undefined;
        sessionStorage.setItem(BOUNCED_KEY, '1');
        const timer = window.setTimeout(() => setEntering(false), 2400);
        return () => window.clearTimeout(timer);
    }, [entering]);

    // idle attention hop: every 10 s, only when nothing else is going on and the tab is visible
    const idle = !entering && !streaming && !hasPending && !dragging;
    useEffect(() => {
        if (!idle) {
            setNudging(false);
            return undefined;
        }
        let stop: number | null = null;
        const timer = window.setInterval(() => {
            if (document.hidden) return;
            setNudging(true);
            stop = window.setTimeout(() => setNudging(false), NUDGE_MS);
        }, NUDGE_EVERY_MS);
        return () => {
            window.clearInterval(timer);
            if (stop) window.clearTimeout(stop);
        };
    }, [idle]);

    useEffect(() => {
        const onResize = () => setPosition(current => clamp(current));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        dragRef.current = { startX: event.clientX, startY: event.clientY, right: position.right, bottom: position.bottom, moved: false };
        (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    };

    const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag) return;
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        if (!drag.moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        drag.moved = true;
        setDragging(true);
        setPosition(clamp({ right: drag.right - dx, bottom: drag.bottom - dy }));
    };

    const onPointerUp = () => {
        const drag = dragRef.current;
        dragRef.current = null;
        setDragging(false);
        if (!drag) return;
        if (drag.moved) {
            setPosition(current => {
                localStorage.setItem(POSITION_KEY, JSON.stringify(current));
                return current;
            });
        } else {
            if (openTimerRef.current) window.clearTimeout(openTimerRef.current);
            openTimerRef.current = window.setTimeout(() => {
                openTimerRef.current = null;
                onOpen();
            }, 230);
        }
    };

    const reset = useCallback(() => {
        if (openTimerRef.current) {
            window.clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
        }
        localStorage.removeItem(POSITION_KEY);
        setPosition(DEFAULT_POSITION);
    }, []);

    useEffect(
        () => () => {
            if (openTimerRef.current) window.clearTimeout(openTimerRef.current);
        },
        [],
    );

    let mood: 'enter' | 'idle' | 'think' | 'wave' | 'alert' | 'nudge' = 'idle';
    if (entering) mood = 'enter';
    else if (hasPending) mood = 'alert';
    else if (streaming) mood = 'think';
    else if (hover) mood = 'wave';
    else if (nudging) mood = 'nudge';

    let bubble = '';
    if (hasPending) bubble = 'Waiting for your approval';
    else if (streaming) bubble = `${activity.label || 'Thinking'}…`;
    // on hover the bubble says what clicking does, not just "ask me anything"
    const showCard = !bubble && hover && !dragging;

    return (
        <div
            className={cn(styles.mascot, styles[mood], { [styles.dragging]: dragging, [styles.hovered]: hover && !dragging })}
            style={{ right: position.right, bottom: position.bottom, width: SIZE.width, height: SIZE.height }}
            role='button'
            tabIndex={0}
            aria-label='Ask Swiftlane'
            title='Click to ask. Drag to move. Double-click to reset.'
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onDoubleClick={reset}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onOpen();
                }
            }}
        >
            <div className={styles.backdrop} aria-hidden='true' />
            {bubble && <div className={styles.bubble}>{bubble}</div>}
            {showCard && (
                <div className={styles.card}>
                    <strong>Ask Swiftlane</strong>
                    <span>Add a user, create a visitor pass, unlock a door, or ask who went where.</span>
                    <em>Click to open · Drag to move · ⌘K anywhere</em>
                </div>
            )}
            {hasPending && <span className={styles.badge}>1</span>}
            <svg width={SIZE.width} height={SIZE.height} viewBox='0 0 120 150' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
                <ellipse className={styles.shadow} cx='60' cy='142' rx='34' ry='6' fill='#111827' opacity='0.2' />
                <g className={styles.legL}>
                    <rect x='40' y='110' width='9' height='22' rx='4.5' fill='#2a10b8' />
                    <rect x='33' y='128' width='20' height='8' rx='4' fill='#111827' />
                </g>
                <g className={styles.legR}>
                    <rect x='71' y='110' width='9' height='22' rx='4.5' fill='#2a10b8' />
                    <rect x='67' y='128' width='20' height='8' rx='4' fill='#111827' />
                </g>
                <g className={styles.body}>
                    {/* the Swiftlane mini logo is the body */}
                    <g transform='translate(20 30) scale(0.078125)'>
                        <path
                            d='M0 20C0 8.95431 8.95431 0 20 0H1004C1015.05 0 1024 8.95431 1024 20V1004C1024 1015.05 1015.05 1024 1004 1024H20C8.95431 1024 0 1015.05 0 1004V20Z'
                            fill='#4119F3'
                        />
                        <path
                            d='M910.226 113.777V910.222H796.448V227.555H227.559V796.444H568.892V682.666H341.337V341.333H682.67V455.111H455.115V568.888H682.67V910.222H113.781V113.777H910.226Z'
                            fill='white'
                        />
                    </g>
                    <g className={styles.eyes}>
                        <circle className={styles.eye} cx='46' cy='42' r='3.2' fill='#111827' />
                        <circle className={styles.eye} cx='60' cy='42' r='3.2' fill='#111827' />
                        <circle cx='47' cy='41' r='1' fill='#fff' />
                        <circle cx='61' cy='41' r='1' fill='#fff' />
                    </g>
                    <path d='M48 50 q5 4 10 0' stroke='#111827' strokeWidth='1.6' fill='none' strokeLinecap='round' />
                    {/* arms last, so they stay visible when raised over the head */}
                    <g className={styles.armL}>
                        <rect x='-12' y='66' width='34' height='8' rx='4' fill='#2a10b8' />
                        <circle cx='-14' cy='70' r='6.5' fill='#ffd9b3' />
                    </g>
                    <g className={styles.armR}>
                        <rect x='98' y='66' width='34' height='8' rx='4' fill='#2a10b8' />
                        <circle cx='134' cy='70' r='6.5' fill='#ffd9b3' />
                    </g>
                </g>
            </svg>
        </div>
    );
};

export default Mascot;
